// 韻鏡：単一画面アプリ。入力→読み確認→さがし方→結果を縦一列で。
// 結果は内部スクロールせずページスクロールに一本化する（ResultFlow の粘着ツールバーが追従）。
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchState } from './ui/useSearchState'
import {
  SearchInput,
  ReadingChips,
  VowelKeyDisplay,
  ModeSelector,
  DepthSlider,
  AdvancedPanel,
} from './ui/Controls'
import { ResultFlow } from './ui/ResultFlow'
import { Header, Onboarding, EmptyResults, CopyToast } from './ui/Chrome'
import { KeepBar, useKeep } from './ui/KeepBar'
import { copyText } from './ui/util'
import { toMorae } from './core/vowel'
import { DEFAULT_FLAGS } from './core/toggles'

export function App() {
  const s = useSearchState()
  const [toast, setToast] = useState(false)
  const toastTimer = useRef<number | undefined>(undefined)
  const keep = useKeep()

  const showToast = useCallback(() => {
    setToast(true)
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(false), 1200)
  }, [])

  const onCopyAndKeep = useCallback(
    async (text: string) => {
      const ok = await copyText(text)
      if (!ok) return
      keep.add(text)
      showToast()
    },
    [keep, showToast],
  )

  const onCopyAll = useCallback(
    async (text: string) => {
      const ok = await copyText(text)
      if (!ok) return
      showToast()
    },
    [showToast],
  )

  const queryMora = useMemo(() => toMorae(s.reading).length, [s.reading])

  const hasInput = s.reading.length > 0
  const showResults = hasInput && s.results.length > 0
  const showEmpty = hasInput && s.workerReady && s.total === 0 && s.readingStatus === 'ready'
  const isLoose =
    s.flags.choon === 'ignore' && s.flags.hatsuon === 'exclude' && s.flags.sokuon === 'skip'

  return (
    <div className="min-h-full bg-paper">
      <Header meta={s.meta} />

      <div className="mx-auto w-full max-w-4xl px-4 pb-16">
        {/* 入力 */}
        <section className="space-y-3 pt-4">
          <SearchInput value={s.rawInput} onChange={s.setRawInput} />
          <ReadingChips tokens={s.tokens} status={s.readingStatus} onEdit={s.setTokenReading} />
          <VowelKeyDisplay reading={s.reading} flags={s.flags} />
        </section>

        {/* さがし方 */}
        <section className="mt-3 space-y-3 rounded-2xl border border-line bg-card p-3 shadow-sm">
          <ModeSelector mode={s.rhymeMode} onChange={s.setRhymeMode} />
          {s.rhymeMode !== 'exact' && s.rhymeMode !== 'mid' && (
            <DepthSlider
              depth={s.depth}
              maxDepth={s.maxDepth}
              queryKey={s.queryKey}
              mode={s.rhymeMode}
              onChange={s.setDepth}
            />
          )}
          <AdvancedPanel
            rhymeMode={s.rhymeMode}
            slack={s.slack}
            onSlackChange={s.setSlack}
            flags={s.flags}
            onFlagsChange={s.setFlags}
            posLegend={s.meta?.posLegend ?? []}
            filter={s.filter}
            onFilter={s.setFilter}
          />
        </section>

        {/* 結果 / 空状態 / オンボーディング */}
        {showResults ? (
          <ResultFlow
            results={s.results}
            total={s.total}
            tookMs={s.tookMs}
            queryMora={queryMora}
            requestedDepth={s.depth}
            usedDepth={s.usedDepth}
            posLegend={s.meta?.posLegend ?? []}
            combos={s.combos}
            sort={s.sort}
            onSort={s.setSort}
            onCopy={onCopyAndKeep}
          />
        ) : showEmpty ? (
          <EmptyResults
            canShallower={s.rhymeMode !== 'exact' && s.rhymeMode !== 'mid' && s.depth > 1}
            onShallower={() => s.setDepth(s.depth - 1)}
            canLoosen={!isLoose}
            onLoosen={() => s.setFlags({ ...DEFAULT_FLAGS, choon: 'ignore' })}
          />
        ) : !s.workerReady && hasInput ? (
          <div className="px-4 py-10 text-center text-sm text-mut">
            辞書を読み込み中…（初回のみ・以降はオフラインで動きます）
          </div>
        ) : (
          <Onboarding onPick={s.runExample} />
        )}
      </div>

      <CopyToast show={toast} raised={keep.items.length > 0} />
      <KeepBar items={keep.items} onRemove={keep.remove} onClear={keep.clear} onCopyAll={onCopyAll} />
    </div>
  )
}
